import { useEffect, useMemo, useState } from "react";
import "./ReportsSectionCompact.css";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { useAuth } from "../context/AuthContext";
import { getSalonTimezone } from "../utils/salonTimezone";
import InfoTooltip from "../components/common/InfoTooltip";
import ReportPageHeader from "../components/reports/ReportPageHeader";
import ReportHelpCard from "../components/reports/ReportHelpCard";
import DateRangeErrorBanner from "../components/common/DateRangeErrorBanner";
import {
  buildSuperAdminReportHelpGuide,
  getSuperAdminHelpAnchors,
} from "../constants/superAdminReportHelpGuides";
import {
  BTN_OUTLINE,
  BTN_PRIMARY,
  FIELD_CLASS,
  LABEL_TEXT,
} from "../ui/modalStyles";
import {
  getSuperAdminRewardsFilters,
  postSuperAdminRewardsSummary,
} from "../api/superadminReports";
import useHelpGuideNavigation from "../hooks/useHelpGuideNavigation";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isoWeek);

const DATE_FORMAT = "YYYY-MM-DD";

const QUICK_PICK_OPTIONS = [
  { key: "day", label: "Day" },
  { key: "week", label: "Week" },
  { key: "month", label: "Month" },
  { key: "year", label: "Year" },
];

const QUICK_PICK_BASE =
  "h-9 px-4 rounded-full text-sm font-semibold transition";
const QUICK_PICK_ACTIVE = "bg-rose-500 text-white";
const QUICK_PICK_INACTIVE =
  "border border-slate-300 text-slate-700 hover:border-slate-400";

const SCOPE_OPTIONS = [
  { value: "all", label: "All Salons" },
  { value: "business", label: "Business" },
  { value: "salon", label: "Salon" },
];

const TOOLTIP_TEXT = {
  scope:
    "Choose report reach: all salons, one business, or one salon. Dates are computed in salon local time.",
  business:
    "Optional for All Salons. Required for Business scope. If both business and salon are set, salon must belong to that business.",
  salon:
    "Required for Salon scope. In Business scope, optionally pick one salon to narrow results.",
  show_scope_columns:
    "Include Business and Salon columns in Top lists for broader scopes.",
  date_from: "Start date for this report, in salon local time.",
  date_to: "End date for this report, in salon local time.",
  quick_picks: "Pick a quick range like this week or this month.",
  earned_points: "Points given to clients when tickets are closed.",
  redeemed_points: "Points clients used to discount tickets.",
  net_points: "Earned points minus redeemed points.",
  earned_value: "Dollar value of earned points.",
  redeemed_value: "Dollar value of redeemed points.",
  net_value: "Dollar value of net points change.",
  referral_bonus: "Bonus points given when a referred client completes their first paid ticket.",
  referral_redeemed: "Referral bonus points used by clients. If not tracked, this shows N/A.",
  referral_count: "Number of referral bonuses awarded in this range.",
  redemption_rate: "Percent of earned points that were redeemed.",
  active_clients: "Clients who had any rewards activity in this range.",
  earn_events: "Number of earning transactions in this range.",
  redeem_events: "Number of redemption transactions in this range.",
  avg_earned: "Average points earned per client who earned points.",
  avg_redeemed: "Average points redeemed per client who redeemed points.",
  points_liability:
    "Dollar value of all unredeemed points right now (computed per salon point value where needed).",
  point_value: "Point value from rewards settings. May vary by salon.",
  top_unused: "Clients with the most available points right now.",
  top_redeemed: "Clients who redeemed the most points in this date range.",
  top_referral_bonus: "Clients who earned the most referral bonus points in this date range.",
  business_column: "Business name for this row.",
  salon_column: "Salon name for this row.",
  client_column: "Client name.",
  points_column: "Total points for this list.",
  value_column: "Dollar value based on salon point value settings.",
};

const HELP_GUIDE = buildSuperAdminReportHelpGuide({
  reportKey: "rewards",
  title: "Rewards Analytics (Super Admin)",
  subtitle:
    "Track points earned, redeemed, and balances across the selected scope.",
  summaryLabel: "Rewards Summary",
  detailsLabel: "Rewards Details",
  hasExport: false,
});

const HELP_ANCHORS = getSuperAdminHelpAnchors("rewards");

const isInvalidDateRange = (startValue, endValue) => {
  if (!startValue || !endValue) return false;
  return endValue < startValue;
};

const formatPoints = (value) => {
  const num = Number(value ?? 0);
  if (!Number.isFinite(num)) return "--";
  return Math.round(num).toLocaleString();
};

const formatNumber = (value, digits = 0) => {
  if (value === null || value === undefined || value === "") return "--";
  const num = Number(value);
  if (!Number.isFinite(num)) return "--";
  return num.toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
};

const formatMoney = (value) => {
  const num = Number(value ?? 0);
  if (!Number.isFinite(num)) return "$0.00";
  const fixed = Math.abs(num).toFixed(2);
  return num < 0 ? `-$${fixed}` : `$${fixed}`;
};

const formatPercent = (value) => {
  if (value === null || value === undefined || value === "") return "--";
  const num = Number(value);
  if (!Number.isFinite(num)) return "--";
  return `${(num * 100).toFixed(1)}%`;
};

const parseSalonDate = (value, timeZone) => {
  if (!value || !timeZone) return null;
  const parsed = dayjs.tz(value, DATE_FORMAT, timeZone);
  return parsed.isValid() ? parsed : null;
};

const buildQuickPickRange = ({ pick, timeZone, salonTodayIso }) => {
  if (!timeZone || !pick) return null;
  const base =
    parseSalonDate(salonTodayIso, timeZone) || dayjs().tz(timeZone);
  if (!base?.isValid?.()) return null;
  let start = base;
  let end = base;
  if (pick === "week") {
    start = base.startOf("isoWeek");
    end = base.endOf("isoWeek");
  } else if (pick === "month") {
    start = base.startOf("month");
    end = base.endOf("month");
  } else if (pick === "year") {
    start = base.startOf("year");
    end = base.endOf("year");
  }
  return {
    start: start.format(DATE_FORMAT),
    end: end.format(DATE_FORMAT),
  };
};

const CARD_CLASS =
  "rounded-2xl border border-slate-200 bg-white p-4 shadow-sm";

const normalizeId = (value) => {
  if (value === null || value === undefined || value === "") return "";
  return String(value);
};

const asNumberOrUndefined = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : undefined;
};

export default function SuperAdminRewardsAnalytics() {
  const { currentSalon, is_super_admin, loading: authLoading } = useAuth();
  const { jumpToSectionWithHighlight, getSectionClassName } = useHelpGuideNavigation();
  const currentSalonTimezone = useMemo(
    () => getSalonTimezone(currentSalon) || "America/New_York",
    [currentSalon]
  );

  const [scope, setScope] = useState("all");
  const [businessId, setBusinessId] = useState("");
  const [salonId, setSalonId] = useState("");
  const [showScopeColumns, setShowScopeColumns] = useState(true);
  const [filtersLoading, setFiltersLoading] = useState(false);
  const [businesses, setBusinesses] = useState([]);
  const [salons, setSalons] = useState([]);
  const [pointMeta, setPointMeta] = useState({
    point_value_mode: "single",
    point_value: 0,
    point_value_min: 0,
    point_value_max: 0,
  });

  const businessMap = useMemo(() => {
    const map = new Map();
    (businesses || []).forEach((business) => {
      if (!business) return;
      map.set(String(business.id), business);
    });
    return map;
  }, [businesses]);

  const salonMap = useMemo(() => {
    const map = new Map();
    (salons || []).forEach((salon) => {
      if (!salon) return;
      map.set(String(salon.id), salon);
    });
    return map;
  }, [salons]);

  const anchorTimezone = useMemo(() => {
    if (salonId) {
      const selectedSalon = salonMap.get(String(salonId));
      if (selectedSalon?.timezone) return selectedSalon.timezone;
    }
    if (businessId) {
      const selectedBusiness = businessMap.get(String(businessId));
      if (selectedBusiness?.timezone) return selectedBusiness.timezone;
    }
    return currentSalonTimezone;
  }, [salonId, salonMap, businessId, businessMap, currentSalonTimezone]);

  const salonTodayIso = useMemo(() => {
    if (!anchorTimezone) return "";
    const base = dayjs().tz(anchorTimezone);
    return base.isValid() ? base.format(DATE_FORMAT) : "";
  }, [anchorTimezone]);

  const defaultRange = useMemo(() => {
    const base = parseSalonDate(salonTodayIso, anchorTimezone);
    if (!base) return { start: "", end: "" };
    return {
      start: base.startOf("month").format(DATE_FORMAT),
      end: base.format(DATE_FORMAT),
    };
  }, [salonTodayIso, anchorTimezone]);

  const [startDate, setStartDate] = useState(defaultRange.start);
  const [endDate, setEndDate] = useState(defaultRange.end);
  const [quickPick, setQuickPick] = useState(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [dateRangeError, setDateRangeError] = useState("");

  useEffect(() => {
    if (defaultRange.start && !startDate) {
      setStartDate(defaultRange.start);
    }
    if (defaultRange.end && !endDate) {
      setEndDate(defaultRange.end);
    }
  }, [defaultRange, startDate, endDate]);

  useEffect(() => {
    if (isInvalidDateRange(startDate, endDate)) {
      setDateRangeError("End date can't be before start date.");
    } else {
      setDateRangeError("");
    }
  }, [startDate, endDate]);

  useEffect(() => {
    if (!quickPick) return;
    const range = buildQuickPickRange({
      pick: quickPick,
      timeZone: anchorTimezone,
      salonTodayIso,
    });
    if (!range) return;
    if (range.start !== startDate || range.end !== endDate) {
      setQuickPick(null);
    }
  }, [quickPick, anchorTimezone, salonTodayIso, startDate, endDate]);

  useEffect(() => {
    setShowScopeColumns(scope !== "salon");
  }, [scope]);

  useEffect(() => {
    if (!is_super_admin || authLoading) return undefined;
    let mounted = true;

    const loadFilters = async () => {
      setFiltersLoading(true);
      try {
        const data = await getSuperAdminRewardsFilters({
          scope,
          business_id: asNumberOrUndefined(businessId),
          salon_id: asNumberOrUndefined(salonId),
        });
        if (!mounted) return;

        const nextBusinesses = Array.isArray(data?.businesses)
          ? data.businesses
          : [];
        const nextSalons = Array.isArray(data?.salons) ? data.salons : [];
        setBusinesses(nextBusinesses);
        setSalons(nextSalons);
        setPointMeta({
          point_value_mode: data?.point_value_mode || "single",
          point_value: data?.point_value,
          point_value_min: data?.point_value_min,
          point_value_max: data?.point_value_max,
        });

        if (
          businessId &&
          !nextBusinesses.some(
            (item) => normalizeId(item?.id) === normalizeId(businessId),
          )
        ) {
          setBusinessId("");
        }
        if (
          salonId &&
          !nextSalons.some(
            (item) => normalizeId(item?.id) === normalizeId(salonId),
          )
        ) {
          setSalonId("");
        }
      } catch (err) {
        if (!mounted) return;
        setError(err?.message || "Unable to load report scope filters.");
        setBusinesses([]);
        setSalons([]);
      } finally {
        if (mounted) setFiltersLoading(false);
      }
    };

    loadFilters();
    return () => {
      mounted = false;
    };
  }, [is_super_admin, authLoading, scope, businessId, salonId]);

  const filteredSalons = useMemo(() => {
    if (!businessId) return salons || [];
    return (salons || []).filter(
      (salon) => normalizeId(salon?.business_id) === normalizeId(businessId),
    );
  }, [salons, businessId]);

  const renderFieldLabel = (label, tooltipKey) => (
    <div className="flex items-center gap-1.5">
      <label className={LABEL_TEXT}>{label}</label>
      {tooltipKey ? (
        <InfoTooltip text={TOOLTIP_TEXT[tooltipKey]} label={label} />
      ) : null}
    </div>
  );

  const handleQuickPick = (pickKey) => {
    const range = buildQuickPickRange({
      pick: pickKey,
      timeZone: anchorTimezone,
      salonTodayIso,
    });
    if (!range) return;
    setQuickPick(pickKey);
    setStartDate(range.start);
    setEndDate(range.end);
  };

  const handleScopeChange = (value) => {
    setScope(value);
    if (value === "all") {
      setSalonId("");
    }
    if (value === "business" && businessId && salonId) {
      const selectedSalon = salons.find(
        (item) => normalizeId(item?.id) === normalizeId(salonId),
      );
      if (
        selectedSalon &&
        normalizeId(selectedSalon.business_id) !== normalizeId(businessId)
      ) {
        setSalonId("");
      }
    }
  };

  const handleBusinessChange = (value) => {
    setBusinessId(value);
    if (!value && scope !== "salon") {
      setSalonId("");
      return;
    }
    if (salonId) {
      const selectedSalon = salons.find(
        (item) => normalizeId(item?.id) === normalizeId(salonId),
      );
      if (
        selectedSalon &&
        value &&
        normalizeId(selectedSalon.business_id) !== normalizeId(value)
      ) {
        setSalonId("");
      }
    }
  };

  const handleSalonChange = (value) => {
    setSalonId(value);
    if (!value) return;
    const selectedSalon = salons.find(
      (item) => normalizeId(item?.id) === normalizeId(value),
    );
    if (selectedSalon?.business_id) {
      setBusinessId(String(selectedSalon.business_id));
    }
  };

  const handleRun = async () => {
    if (scope === "business" && !businessId) {
      setError("Select a business for Business scope.");
      return;
    }
    if (scope === "salon" && !salonId) {
      setError("Select a salon for Salon scope.");
      return;
    }
    if (!startDate || !endDate || isInvalidDateRange(startDate, endDate)) {
      setDateRangeError("End date can't be before start date.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const data = await postSuperAdminRewardsSummary({
        scope,
        business_id: asNumberOrUndefined(businessId),
        salon_id: asNumberOrUndefined(salonId),
        date_from: startDate,
        date_to: endDate,
        quick_pick: quickPick,
        include_business_salon_cols: showScopeColumns,
        limit_top: 10,
      });
      setReport(data || null);
    } catch (err) {
      setError("Unable to load rewards analytics.");
      setReport(null);
    } finally {
      setLoading(false);
    }
  };
  const handleAskAi = () => {
    if (!startDate || !endDate) return;
    const prefillMessage = `Analyze Super Admin Rewards Analytics from ${startDate} to ${endDate} for scope ${scope}. Start with: points earned, points redeemed, net points change, redemption rate, points liability, and top unused rewards.`;
    if (typeof window === "undefined") return;
    window.dispatchEvent(
      new CustomEvent("sbin:open-ai-assistant", {
        detail: {
          prefillMessage,
          context: {
            intent: "superadmin_rewards_analytics",
            scope,
            business_id: asNumberOrUndefined(businessId),
            salon_id: asNumberOrUndefined(salonId),
            start_date: startDate,
            end_date: endDate,
          },
        },
      })
    );
  };

  const topUnused = report?.top_clients_unused_rewards || [];
  const topRedeemed = report?.top_clients_redeemed_in_range || [];
  const topReferralBonus = report?.top_clients_referral_bonus_in_range || [];

  const renderTopClientsTable = (title, tooltipKey, rows, emptyMessage) => (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2 text-base md:text-lg font-semibold text-slate-900">
        <span>{title}</span>
        <InfoTooltip text={TOOLTIP_TEXT[tooltipKey]} label={title} />
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              {showScopeColumns ? (
                <>
                  <th className="px-4 py-3 font-semibold">
                    <div className="flex items-center gap-1.5">
                      <span>Business</span>
                      <InfoTooltip
                        text={TOOLTIP_TEXT.business_column}
                        label="Business"
                      />
                    </div>
                  </th>
                  <th className="px-4 py-3 font-semibold">
                    <div className="flex items-center gap-1.5">
                      <span>Salon</span>
                      <InfoTooltip
                        text={TOOLTIP_TEXT.salon_column}
                        label="Salon"
                      />
                    </div>
                  </th>
                </>
              ) : null}
              <th className="px-4 py-3 font-semibold">
                <div className="flex items-center gap-1.5">
                  <span>Client</span>
                  <InfoTooltip
                    text={TOOLTIP_TEXT.client_column}
                    label="Client"
                  />
                </div>
              </th>
              <th className="px-4 py-3 font-semibold">
                <div className="flex items-center gap-1.5">
                  <span>Points</span>
                  <InfoTooltip
                    text={TOOLTIP_TEXT.points_column}
                    label="Points"
                  />
                </div>
              </th>
              <th className="px-4 py-3 font-semibold">
                <div className="flex items-center gap-1.5">
                  <span>$ Value</span>
                  <InfoTooltip
                    text={TOOLTIP_TEXT.value_column}
                    label="$ Value"
                  />
                </div>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.length === 0 ? (
              <tr>
                <td
                  colSpan={showScopeColumns ? 5 : 3}
                  className="px-4 py-4 text-sm md:text-base text-slate-500"
                >
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              rows.map((row, index) => (
                <tr
                  key={
                    row.row_key ||
                    `${title}-${row.client_id || "client"}-${row.salon_id || "salon"}-${index}`
                  }
                >
                  {showScopeColumns ? (
                    <>
                      <td className="px-4 py-3 text-slate-700">
                        {row.business_name || "--"}
                      </td>
                      <td className="px-4 py-3 text-slate-700">
                        {row.salon_name || "--"}
                      </td>
                    </>
                  ) : null}
                  <td className="px-4 py-3 text-slate-900 font-semibold">
                    {row.client_name || "Client"}
                  </td>
                  <td className="px-4 py-3 text-slate-700">
                    {formatPoints(row.value)}
                  </td>
                  <td className="px-4 py-3 text-slate-700">
                    {formatMoney(row.value_dollars)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  const referralRedeemedPoints = report?.referral_points_redeemed_total;
  const referralRedeemedValue = report?.referral_points_redeemed_value_total;
  const selectedSalonTimezone = useMemo(() => {
    if (!salonId) return "";
    const selectedSalon = salons.find(
      (item) => normalizeId(item?.id) === normalizeId(salonId),
    );
    return selectedSalon?.timezone || "";
  }, [salonId, salons]);

  const timezoneLine =
    scope === "salon"
      ? `Salon timezone: ${selectedSalonTimezone || report?.timezone || anchorTimezone}`
      : "Dates use each salon's local timezone.";

  const effectivePointMeta = {
    point_value_mode: report?.point_value_mode || pointMeta.point_value_mode,
    point_value:
      report?.point_value !== undefined && report?.point_value !== null
        ? report.point_value
        : pointMeta.point_value,
    point_value_min:
      report?.point_value_min !== undefined && report?.point_value_min !== null
        ? report.point_value_min
        : pointMeta.point_value_min,
    point_value_max:
      report?.point_value_max !== undefined && report?.point_value_max !== null
        ? report.point_value_max
        : pointMeta.point_value_max,
  };

  let pointValueLine = `Point value: ${formatMoney(
    effectivePointMeta.point_value || 0,
  )} per point`;
  if (scope !== "salon" && effectivePointMeta.point_value_mode === "varies") {
    if (
      Number.isFinite(Number(effectivePointMeta.point_value_min)) &&
      Number.isFinite(Number(effectivePointMeta.point_value_max))
    ) {
      pointValueLine = `Point value: varies by salon (${formatMoney(
        effectivePointMeta.point_value_min,
      )} to ${formatMoney(effectivePointMeta.point_value_max)} per point)`;
    } else {
      pointValueLine = "Point value: varies by salon";
    }
  }

  if (!is_super_admin && !authLoading) {
    return (
      <div className="reportSectionShell min-h-screen bg-slate-100">
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          Super Admin access required.
        </div>
      </div>
    );
  }

  return (
    <div className="reportSectionShell min-h-screen bg-slate-100">
      <div className="reportSectionWide report-section-spacious report-section-compact">
        <div className="rounded-3xl border border-slate-200 bg-white shadow-2xl overflow-hidden">
          <ReportPageHeader
            title="Rewards Analytics"
            closeTo="/superadmin/reports"
            titleAddon={
              <InfoTooltip
                text="Track points earned, redeemed, referrals, and top clients across selected scope."
                label="Rewards Analytics"
              />
            }
          />

          <div className="px-6 py-6 space-y-6">
            <div
              id={HELP_ANCHORS.overview}
              className={`rounded-2xl ${getSectionClassName(HELP_ANCHORS.overview)}`}
            >
              <ReportHelpCard
                title={HELP_GUIDE.title}
                subtitle={HELP_GUIDE.subtitle}
                sections={HELP_GUIDE.sections}
                troubleshooting={HELP_GUIDE.troubleshooting}
                onJumpToSection={jumpToSectionWithHighlight}
              />
            </div>
            <div
              id={HELP_ANCHORS.filters}
              className={`rounded-2xl border border-slate-200 bg-white shadow-sm p-5 space-y-4 ${getSectionClassName(
                HELP_ANCHORS.filters
              )}`}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                <div>
                  {renderFieldLabel("Scope", "scope")}
                  <select
                    value={scope}
                    onChange={(event) => handleScopeChange(event.target.value)}
                    className={`${FIELD_CLASS} w-full`}
                  >
                    {SCOPE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  {renderFieldLabel("Business", "business")}
                  <select
                    value={businessId}
                    onChange={(event) => handleBusinessChange(event.target.value)}
                    className={`${FIELD_CLASS} w-full`}
                  >
                    <option value="">All businesses</option>
                    {(businesses || []).map((business) => (
                      <option key={business.id} value={business.id}>
                        {business.name || `Business ${business.id}`}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  {renderFieldLabel("Salon", "salon")}
                  <select
                    value={salonId}
                    onChange={(event) => handleSalonChange(event.target.value)}
                    disabled={
                      filtersLoading ||
                      scope === "all" ||
                      (scope === "business" && !businessId)
                    }
                    className={`${FIELD_CLASS} w-full`}
                  >
                    <option value="">All salons</option>
                    {(filteredSalons || []).map((salon) => (
                      <option key={salon.id} value={salon.id}>
                        {salon.name || `Salon ${salon.id}`}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex items-end">
                  <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      checked={showScopeColumns}
                      onChange={(event) =>
                        setShowScopeColumns(event.target.checked)
                      }
                      className="h-4 w-4 rounded border-slate-300 text-rose-500 focus:ring-rose-500"
                      disabled={scope === "salon"}
                    />
                    <span>Show Business/Salon columns</span>
                    <InfoTooltip
                      text={TOOLTIP_TEXT.show_scope_columns}
                      label="Show Business/Salon columns"
                    />
                  </label>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  {renderFieldLabel("Date From", "date_from")}
                  <input
                    type="date"
                    value={startDate}
                    onChange={(event) => setStartDate(event.target.value)}
                    className={`${FIELD_CLASS} w-full`}
                  />
                </div>
                <div>
                  {renderFieldLabel("Date To", "date_to")}
                  <input
                    type="date"
                    value={endDate}
                    onChange={(event) => setEndDate(event.target.value)}
                    className={`${FIELD_CLASS} w-full`}
                  />
                </div>
                <div>
                  {renderFieldLabel("Quick Picks", "quick_picks")}
                  <div className="mt-2 flex flex-wrap gap-2">
                    {QUICK_PICK_OPTIONS.map((option) => {
                      const isActive = quickPick === option.key;
                      return (
                        <button
                          key={option.key}
                          type="button"
                          onClick={() => handleQuickPick(option.key)}
                          className={`${QUICK_PICK_BASE} ${
                            isActive ? QUICK_PICK_ACTIVE : QUICK_PICK_INACTIVE
                          }`}
                        >
                          {option.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="text-sm text-slate-500">{timezoneLine}</div>
                <div className="flex flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={handleAskAi}
                      className={BTN_OUTLINE}
                      disabled={loading}
                    >
                      Ask AI
                    </button>
                    <InfoTooltip
                      text="Ask AI questions about this Rewards Analytics report."
                      label="Ask AI"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setScope("all");
                      setBusinessId("");
                      setSalonId("");
                      setShowScopeColumns(true);
                      setStartDate(defaultRange.start);
                      setEndDate(defaultRange.end);
                      setQuickPick(null);
                      setReport(null);
                      setError("");
                      setDateRangeError("");
                    }}
                    className={BTN_OUTLINE}
                    disabled={loading || filtersLoading}
                  >
                    Reset
                  </button>
                  <button
                    type="button"
                    onClick={handleRun}
                    className={BTN_PRIMARY}
                    disabled={loading || filtersLoading}
                  >
                    {loading ? "Running..." : "Run Report"}
                  </button>
                </div>
              </div>

              <div className="text-sm text-slate-500 flex items-center gap-2">
                <span>{pointValueLine}</span>
                <InfoTooltip text={TOOLTIP_TEXT.point_value} label="Point value" />
              </div>

              <DateRangeErrorBanner
                show={Boolean(dateRangeError)}
                message={dateRangeError}
              />
            </div>

            <div
              id={HELP_ANCHORS.export}
              className={`rounded-2xl border border-slate-200 bg-white p-4 ${getSectionClassName(
                HELP_ANCHORS.export
              )}`}
            >
              <div className="text-base font-semibold text-slate-900">Export</div>
              <div className="mt-1 text-sm text-slate-600">
                This report does not support export.
              </div>
            </div>

            {filtersLoading ? (
              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-600 text-sm">
                Loading scope filters...
              </div>
            ) : null}

            {error ? (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700 text-sm md:text-base">
                {error}
              </div>
            ) : null}

            {!report && !loading ? (
              <div className="rounded-2xl border border-slate-200 bg-white px-4 py-6 text-center text-slate-600">
                Run the report to see rewards analytics.
              </div>
            ) : null}

            {report ? (
              <div className="space-y-6">
                <div
                  id={HELP_ANCHORS.kpis}
                  className={`grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6 gap-4 ${getSectionClassName(
                    HELP_ANCHORS.kpis
                  )}`}
                >
                  <div className={CARD_CLASS}>
                    <div className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-slate-500">
                      <span>Total points earned</span>
                      <InfoTooltip
                        text={TOOLTIP_TEXT.earned_points}
                        label="Total points earned"
                      />
                    </div>
                    <div className="mt-2 text-2xl font-semibold text-slate-900">
                      {formatPoints(report.earned_points_total)} pts
                    </div>
                    <div className="text-sm text-slate-500">
                      {formatMoney(report.earned_value_total)} value
                    </div>
                  </div>
                  <div className={CARD_CLASS}>
                    <div className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-slate-500">
                      <span>Total points redeemed</span>
                      <InfoTooltip
                        text={TOOLTIP_TEXT.redeemed_points}
                        label="Total points redeemed"
                      />
                    </div>
                    <div className="mt-2 text-2xl font-semibold text-slate-900">
                      {formatPoints(report.redeemed_points_total)} pts
                    </div>
                    <div className="text-sm text-slate-500">
                      {formatMoney(report.redeemed_value_total)} value
                    </div>
                  </div>
                  <div className={CARD_CLASS}>
                    <div className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-slate-500">
                      <span>Net points change</span>
                      <InfoTooltip
                        text={TOOLTIP_TEXT.net_points}
                        label="Net points change"
                      />
                    </div>
                    <div className="mt-2 text-2xl font-semibold text-slate-900">
                      {formatPoints(report.net_points_change)} pts
                    </div>
                    <div className="text-sm text-slate-500">
                      {formatMoney(report.net_value_change)} value
                    </div>
                  </div>
                  <div className={CARD_CLASS}>
                    <div className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-slate-500">
                      <span>Referral bonus</span>
                      <InfoTooltip
                        text={TOOLTIP_TEXT.referral_bonus}
                        label="Referral bonus"
                      />
                    </div>
                    <div className="mt-2 text-2xl font-semibold text-slate-900">
                      {formatPoints(report.referral_bonus_points_earned_total)} pts
                    </div>
                    <div className="text-sm text-slate-500">
                      {formatNumber(report.referrals_count)} referrals
                    </div>
                  </div>
                  <div className={CARD_CLASS}>
                    <div className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-slate-500">
                      <span>Redemption rate</span>
                      <InfoTooltip
                        text={TOOLTIP_TEXT.redemption_rate}
                        label="Redemption rate"
                      />
                    </div>
                    <div className="mt-2 text-2xl font-semibold text-slate-900">
                      {formatPercent(report.redemption_rate_in_range)}
                    </div>
                    <div className="text-sm text-slate-500">
                      Redeemed vs earned
                    </div>
                  </div>
                  <div className={CARD_CLASS}>
                    <div className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-slate-500">
                      <span>Referral points redeemed</span>
                      <InfoTooltip
                        text={TOOLTIP_TEXT.referral_redeemed}
                        label="Referral points redeemed"
                      />
                    </div>
                    <div className="mt-2 text-2xl font-semibold text-slate-900">
                      {referralRedeemedPoints === null ||
                      referralRedeemedPoints === undefined
                        ? "N/A"
                        : `${formatPoints(referralRedeemedPoints)} pts`}
                    </div>
                    <div className="text-sm text-slate-500">
                      {referralRedeemedValue === null ||
                      referralRedeemedValue === undefined
                        ? "Not tracked"
                        : `${formatMoney(referralRedeemedValue)} value`}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6 gap-4">
                  <div className={CARD_CLASS}>
                    <div className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-slate-500">
                      <span>Active reward clients</span>
                      <InfoTooltip
                        text={TOOLTIP_TEXT.active_clients}
                        label="Active reward clients"
                      />
                    </div>
                    <div className="mt-2 text-2xl font-semibold text-slate-900">
                      {formatNumber(report.active_reward_clients_count)}
                    </div>
                  </div>
                  <div className={CARD_CLASS}>
                    <div className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-slate-500">
                      <span>Earn events</span>
                      <InfoTooltip
                        text={TOOLTIP_TEXT.earn_events}
                        label="Earn events"
                      />
                    </div>
                    <div className="mt-2 text-2xl font-semibold text-slate-900">
                      {formatNumber(report.earn_events_count)}
                    </div>
                  </div>
                  <div className={CARD_CLASS}>
                    <div className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-slate-500">
                      <span>Redeem events</span>
                      <InfoTooltip
                        text={TOOLTIP_TEXT.redeem_events}
                        label="Redeem events"
                      />
                    </div>
                    <div className="mt-2 text-2xl font-semibold text-slate-900">
                      {formatNumber(report.redeem_events_count)}
                    </div>
                  </div>
                  <div className={CARD_CLASS}>
                    <div className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-slate-500">
                      <span>Avg earned / client</span>
                      <InfoTooltip
                        text={TOOLTIP_TEXT.avg_earned}
                        label="Avg earned per client"
                      />
                    </div>
                    <div className="mt-2 text-2xl font-semibold text-slate-900">
                      {formatNumber(report.avg_points_earned_per_earning_client, 1)}
                    </div>
                  </div>
                  <div className={CARD_CLASS}>
                    <div className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-slate-500">
                      <span>Avg redeemed / client</span>
                      <InfoTooltip
                        text={TOOLTIP_TEXT.avg_redeemed}
                        label="Avg redeemed per client"
                      />
                    </div>
                    <div className="mt-2 text-2xl font-semibold text-slate-900">
                      {formatNumber(
                        report.avg_points_redeemed_per_redeeming_client,
                        1
                      )}
                    </div>
                  </div>
                  <div className={CARD_CLASS}>
                    <div className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-slate-500">
                      <span>Points liability</span>
                      <InfoTooltip
                        text={TOOLTIP_TEXT.points_liability}
                        label="Points liability"
                      />
                    </div>
                    <div className="mt-2 text-2xl font-semibold text-slate-900">
                      {formatMoney(report.points_liability_estimate)}
                    </div>
                  </div>
                </div>

                <div
                  id={HELP_ANCHORS.details}
                  className={`grid grid-cols-1 xl:grid-cols-3 gap-4 ${getSectionClassName(
                    HELP_ANCHORS.details
                  )}`}
                >
                  {renderTopClientsTable(
                    "Top unused rewards (as of now)",
                    "top_unused",
                    topUnused,
                    "No clients with unused rewards yet."
                  )}
                  {renderTopClientsTable(
                    "Top redeemed rewards (in range)",
                    "top_redeemed",
                    topRedeemed,
                    "No redemptions in this range."
                  )}
                  {renderTopClientsTable(
                    "Top referral bonus earned (in range)",
                    "top_referral_bonus",
                    topReferralBonus,
                    "No referral bonuses in this range."
                  )}
                </div>

                <details className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                  <summary className="cursor-pointer select-none px-4 py-3 flex flex-wrap items-center justify-between gap-3 bg-slate-50">
                    <div className="text-base md:text-lg font-semibold text-slate-900">
                      User Guide
                    </div>
                    <div className="text-xs text-slate-500">
                      What this report means and how to use it
                    </div>
                  </summary>
                  <div className="px-4 py-4 space-y-4 text-sm md:text-base text-slate-600">
                    <div>
                      <div className="font-semibold text-slate-900">
                        What this report measures
                      </div>
                      <p>
                        Rewards activity for your selected scope: points earned,
                        redeemed, referral bonuses, and client-level balances.
                      </p>
                    </div>
                    <div>
                      <div className="font-semibold text-slate-900">
                        KPI quick meanings
                      </div>
                      <ul className="list-disc pl-5 space-y-1">
                        <li>Earned points: points given when tickets close.</li>
                        <li>Redeemed points: points used to discount tickets.</li>
                        <li>Net points: earned points minus redeemed points.</li>
                        <li>Redemption rate: percent of earned points redeemed.</li>
                        <li>Referral bonus: points awarded for referrals.</li>
                        <li>Unused rewards: points clients still have available.</li>
                        <li>Points liability: dollar value of unredeemed points.</li>
                      </ul>
                    </div>
                    <div>
                      <div className="font-semibold text-slate-900">
                        How to use it weekly
                      </div>
                      <p>
                        Compare earned vs redeemed points, watch referral bonuses,
                        and check which clients still have unused points.
                      </p>
                    </div>
                    <div>
                      <div className="font-semibold text-slate-900">
                        How it helps revenue
                      </div>
                      <p>
                        Strong earned activity supports repeat visits, healthy
                        redemption rates show clients are coming back, and referrals
                        grow new customer revenue.
                      </p>
                    </div>
                    <div>
                      <div className="font-semibold text-slate-900">
                        Marketing ideas based on KPIs
                      </div>
                      <ul className="list-disc pl-5 space-y-1">
                        <li>Target clients with unused points and send reminders.</li>
                        <li>Run "use your points" campaigns during slow weeks.</li>
                        <li>Prompt referrers with referral bonus updates.</li>
                      </ul>
                    </div>
                    <div>
                      <div className="font-semibold text-slate-900">
                        Operational tips
                      </div>
                      <ul className="list-disc pl-5 space-y-1">
                        <li>Keep point value sustainable to protect margins.</li>
                        <li>Monitor rewards liability to avoid surprise discounts.</li>
                      </ul>
                    </div>
                  </div>
                </details>
              </div>
            ) : null}

          </div>
        </div>
      </div>
    </div>
  );
}
